import {Box, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import {FC, useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {EmptyTable} from 'src/components/tables/EmptyTable';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {CardList} from 'src/components/common/CardList';
import {ResponsiveTable} from 'src/components/common/ResponsiveTable';
import {BexioAccountItemCard} from './BexioAccountItemCard';
import {BexioAccountsTableProps} from './BexioAccountsTable.types';
import {columnConfigBexioAccounts} from './BexioAccountsTable.tableConfig';
import {addButtonSx, tableContainerSx} from './BexioAccountsTable.sx';
import {bexioAccountsApi, usersApi} from 'src/api';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {BexioAccountDTO, BexioAccountSortField} from 'src/types';
import {NotifyError} from 'src/utils/notification';
import {LinkBexioAccountModal} from './LinkBexioAccountModal';
import {UpdateBexioIdModal} from './UpdateBexioIdModal';
import {useCan} from 'src/permissions/hooks';

export const BexioAccountsTable: FC<BexioAccountsTableProps> = ({
                                                                    data,
                                                                    refetchEmployee,
                                                                }) => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const canLinkBexio = useCan('users.linkBexio');
    const {authInfo} = useAppSelector((state) => state.common);
    const [availableAccounts, setAvailableAccounts] = useState<BexioAccountDTO[]>([]);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [currentUpdateAccount, setCurrentUpdateAccount] = useState<{
        bexioAccountId: number;
        bexioId?: number
    } | null>(null);

    // API hooks
    const [linkTrigger] = usersApi.useLinkToBexioAccountMutation();
    const [unlinkTrigger] = usersApi.useUnlinkFromBexioAccountMutation();
    const [setPrimaryTrigger] = usersApi.useSetPrimaryBexioAccountMutation();
    const [updateBexioIdTrigger] = usersApi.useUpdateUserBexioIdForAccountMutation();

    // Search for available Bexio accounts
    const {data: bexioAccountsData} = bexioAccountsApi.useSearchBexioAccountsQuery({
        data: {
            businessId: businessId || 0,
            sortField: BexioAccountSortField.COMPANY_NAME,
            sortAsc: true,
        },
        params: {
            page: 0,
            size: 100,
        },
    }, {
        skip: !businessId,
    });

    useEffect(() => {
        if (bexioAccountsData?.content) {
            setAvailableAccounts(bexioAccountsData.content);
        }
    }, [bexioAccountsData]);

    // Helper function to handle Bexio API errors
    const handleBexioError = (error: any) => {
        if (error?.status === 400 && error?.data?.message) {
            // Bexio specific error
            const errorMessage = error.data.message;
            if (errorMessage.includes('Invalid auth token')) {
                NotifyError('Bexio authentication error. Please check your Bexio token configuration.');
            } else if (errorMessage.includes('Bexio')) {
                NotifyError(`Bexio error: ${errorMessage.replace('Bexio. ', '')}`);
            } else {
                NotifyError(errorMessage);
            }
        } else if (error?.status === 400) {
            NotifyError('Failed to connect to Bexio service. Please try again later.');
        } else {
            NotifyError('An unexpected error occurred. Please try again.');
        }
    };

    const handleLinkAccount = async (bexioAccountId: number) => {
        if (!data?.id || !businessId) return;

        try {
            await linkTrigger({
                userId: data.id,
                data: {
                    businessId: businessId,
                    bexioAccountId,
                },
            }).unwrap();

            // Success - refetch employee data to get updated Bexio accounts
            if (refetchEmployee) {
                refetchEmployee();
            }
        } catch (error) {
            console.error('Failed to link bexio account:', error);
            handleBexioError(error);
        }
    };

    const handleUnlinkAccount = async (bexioAccountId: number) => {
        if (!data?.id || !businessId) return;

        try {
            await unlinkTrigger({
                userId: data.id,
                data: {
                    businessId: businessId,
                    bexioAccountId,
                },
            }).unwrap();

            // Success - refetch employee data to get updated Bexio accounts
            if (refetchEmployee) {
                refetchEmployee();
            }
        } catch (error) {
            console.error('Failed to unlink bexio account:', error);
            handleBexioError(error);
        }
    };

    const handleMakePrimary = async (bexioAccountId: number) => {
        if (!data?.id || !businessId) return;

        try {
            await setPrimaryTrigger({
                userId: data.id,
                data: {
                    businessId: businessId,
                    bexioAccountId,
                },
            }).unwrap();

            // Success - refetch employee data to get updated Bexio accounts
            if (refetchEmployee) {
                refetchEmployee();
            }
        } catch (error) {
            console.error('Failed to set primary bexio account:', error);
            handleBexioError(error);
        }
    };

    const handleUpdateAccount = (bexioAccountId: number) => {
        // Find the current bexioId for this account
        const account = bexioAccounts.find(acc => acc.bexioAccountId === bexioAccountId);
        setCurrentUpdateAccount({
            bexioAccountId,
            bexioId: account?.bexioId,
        });
        setIsUpdateModalOpen(true);
    };

    const handleUpdateBexioId = async (newBexioId: number | undefined) => {
        if (!data?.id || !businessId || !currentUpdateAccount) return;

        try {
            await updateBexioIdTrigger({
                userId: data.id,
                bexioAccountId: currentUpdateAccount.bexioAccountId,
                data: {
                    businessId: businessId,
                    bexioId: newBexioId,
                },
            }).unwrap();

            // Success - refetch employee data to get updated Bexio accounts
            if (refetchEmployee) {
                refetchEmployee();
            }
        } catch (error) {
            console.error('Failed to update bexio ID:', error);
            handleBexioError(error);
        }
    };

    const handleCloseUpdateModal = () => {
        setIsUpdateModalOpen(false);
        setCurrentUpdateAccount(null);
    };

    // Use actual user's bexio data for linked accounts
    const bexioAccounts = data?.bexioDataList || [];

    // Get linked account IDs to exclude from available accounts in modal
    const linkedAccountIds = bexioAccounts.map(account => account.bexioAccountId).filter(Boolean);

    // Transform data for DataGrid - add id field required by DataGrid
    const tableRows = bexioAccounts.map((account, index) => ({
        id: account.bexioAccountId || index,
        ...account,
    }));

    const handleOpenLinkModal = () => {
        setIsLinkModalOpen(true);
    };

    const handleCloseLinkModal = () => {
        setIsLinkModalOpen(false);
    };

    return (
        // The shared layout, flush: this table lives inside the relationships card, so it brings the
        // rows and the add-row footer but not a second card around them.
        <TableLayout
            flush
            hideFooter
            footerPadded={false}
            footer={canLinkBexio && (
                <Box
                    sx={addButtonSx}
                    onClick={handleOpenLinkModal}
                >
                    <Add sx={{mr: 1, fontSize: '1.25rem'}}/>
                    <Typography
                        variant="body2"
                        color="primary"
                    >
                        <FormattedMessage id="linkNewBexioAccount"/>
                    </Typography>
                </Box>
            )}
        >
            <ResponsiveTable
                responsiveElement={
                    tableRows.length ? (
                        <CardList>
                            {tableRows.map((account) => (
                                <BexioAccountItemCard
                                    key={account.id}
                                    account={account}
                                    onUnlink={handleUnlinkAccount}
                                    onMakePrimary={handleMakePrimary}
                                    onUpdate={handleUpdateAccount}
                                />
                            ))}
                        </CardList>
                    ) : (
                        // What the grid puts in its no-rows overlay, said the same way here.
                        <EmptyTable/>
                    )
                }
                desktopElement={
                    <Table
                        rows={tableRows}
                        hideFooter
                        columns={columnConfigBexioAccounts(handleUnlinkAccount, handleMakePrimary, handleUpdateAccount)}
                        sx={{
                            border: 'none',
                            borderRadius: 0,
                            '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                                outline: 'none !important',
                            },
                        }}
                    />
                }
            />

            {/* Linking an account to a person is its own permission on the server; reading the
                list is not enough to do it — the footer above is gated on it. */}
            <LinkBexioAccountModal
                open={isLinkModalOpen}
                onClose={handleCloseLinkModal}
                onLink={handleLinkAccount}
                availableAccounts={availableAccounts}
                linkedAccountIds={linkedAccountIds}
            />

            <UpdateBexioIdModal
                open={isUpdateModalOpen}
                onClose={handleCloseUpdateModal}
                onUpdate={handleUpdateBexioId}
                currentBexioId={currentUpdateAccount?.bexioId}
            />
        </TableLayout>
    );
};